import React, { useContext } from "react"
import { UserContext } from "./contexts"
import {
  Star,
  Heart,
  ArrowLeft,
  AppStore,
  PlaySolid,
  DollarCircle,
  Clock,
  Trophy,
  Sparks,
  ThumbsUp,
  Community
} from "iconoir-react"
import { Link } from "react-router-dom"
import { settings } from "@/settings"
import mixpanel from "mixpanel-browser"
import { InAppReview } from "@capacitor-community/in-app-review"

const ReviewPage: React.FC = () => {
  const user = useContext(UserContext)

  const handleReviewRequest = async () => {
    try {
      await InAppReview.requestReview()

      mixpanel.track("Review Requested - Manual", {
        source: "review_page",
        user_plan: user.plan,
        platform: settings.IS_ANDROID ? "android" : "ios"
      })

      // Mark that we've prompted this user
      const reviewPromptedKey = `vibrato_review_prompted_${user.id}`
      const lastPromptKey = `vibrato_last_review_prompt_${user.id}`
      localStorage.setItem(reviewPromptedKey, "true")
      localStorage.setItem(lastPromptKey, Date.now().toString())
    } catch (error) {
      console.error("Error requesting review:", error)
      mixpanel.track("Review Request Error", {
        source: "review_page",
        error: error instanceof Error ? error.message : String(error),
        user_plan: user.plan
      })
    }
  }

  const handleWebReview = (platform: string, url: string) => {
    mixpanel.track("Review Link Clicked", {
      source: "review_page",
      platform,
      user_plan: user.plan
    })

    window.open(url, "_blank", "noopener,noreferrer")
  }

  return (
    <div className="h-full w-full p-6 md:p-8 flex flex-col gap-6">
      {/* Back button */}
      <Link
        to="/settings/profile"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors w-fit"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Settings
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-100 rounded-xl">
            <Heart width="24" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Love Vibrato?</h1>
            <p className="text-gray-600">Help us spread the word with a review</p>
          </div>
        </div>
      </div>

      {/* Info section */}
      <div className="bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 p-4 md:p-6 rounded-xl border border-purple-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-purple-500 rounded-lg flex-shrink-0 mt-1">
            <Community className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm md:text-base text-purple-800 font-medium mb-1">
              Join thousands of satisfied users who've transformed their productivity
            </p>
            <p className="text-sm text-purple-700">
              Your review helps other people discover how Vibrato can save them time and money. We truly appreciate you
              taking the time to share your experience.
            </p>
          </div>
        </div>
      </div>

      {/* Native App Review Button */}
      {settings.SHOW_NATIVE_APP && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6 md:p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="p-4 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full">
                  <Star className="w-8 h-8 text-purple-600" />
                </div>
                <div className="absolute -top-1 -right-1">
                  <Sparks className="w-5 h-5 text-yellow-500" />
                </div>
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Rate Vibrato</h2>
            <p className="text-gray-600 mb-6">Share your experience and help others discover Vibrato</p>
            <button
              onClick={handleReviewRequest}
              className="px-8 py-4 bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-600 hover:from-purple-600 hover:via-purple-700 hover:to-indigo-700 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 transition-all duration-300 flex items-center gap-3 mx-auto text-lg"
            >
              <Star className="w-6 h-6 drop-shadow-sm" />
              Leave a Review
            </button>
          </div>
        </div>
      )}

      {/* Web Review Links */}
      {!settings.SHOW_NATIVE_APP && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-6 md:p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">Choose Your Platform</h2>
            <p className="text-gray-600 mb-6 text-center">Leave a review on your preferred platform</p>

            <div className="flex flex-col sm:grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-2xl mx-auto">
              <button
                onClick={() =>
                  handleWebReview(
                    "Apple App Store",
                    "https://apps.apple.com/us/app/vibrato-ai-calls-skip-hold/id6746279853"
                  )
                }
                className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 hover:from-blue-500 hover:via-blue-600 hover:to-blue-700 text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 group min-h-[140px] flex items-center justify-center"
              >
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <AppStore className="w-10 h-10 sm:w-12 sm:h-12 drop-shadow-lg" />
                  </div>
                  <div className="font-bold text-xl sm:text-2xl mb-2 drop-shadow-sm">App Store</div>
                  <div className="text-sm sm:text-base opacity-95 font-medium">Leave a review on the App Store</div>
                </div>
              </button>
              <button
                onClick={() =>
                  handleWebReview(
                    "Google Play Store",
                    "https://play.google.com/store/apps/details?id=com.getvibrato.mobile"
                  )
                }
                className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-green-400 via-green-500 to-green-600 hover:from-green-500 hover:via-green-600 hover:to-green-700 text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 group min-h-[140px] flex items-center justify-center"
              >
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <PlaySolid className="w-10 h-10 sm:w-12 sm:h-12 drop-shadow-lg" />
                  </div>
                  <div className="font-bold text-xl sm:text-2xl mb-2 drop-shadow-sm">Google Play</div>
                  <div className="text-sm sm:text-base opacity-95 font-medium">Leave a review on Google Play</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Thank you message */}
      <div className="bg-gradient-to-r from-yellow-50 via-orange-50 to-pink-50 p-6 md:p-8 rounded-xl border border-yellow-200">
        <div className="text-center">
          <div className="flex justify-center gap-2 mb-4">
            <div className="p-2 bg-yellow-500 rounded-full">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div className="p-2 bg-pink-500 rounded-full">
              <ThumbsUp className="w-6 h-6 text-white" />
            </div>
            <div className="p-2 bg-purple-500 rounded-full">
              <Heart className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-2">
            <Sparks className="w-5 h-5 text-yellow-500" />
            Thank You for Being Amazing!
            <Sparks className="w-5 h-5 text-yellow-500" />
          </h3>
          <p className="text-gray-700 mb-4 text-base">
            Your feedback helps us improve Vibrato and helps others discover our service.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center justify-center gap-2 text-emerald-700">
              <DollarCircle className="w-4 h-4" />
              <span className="font-medium">Saving Money</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-blue-700">
              <Clock className="w-4 h-4" />
              <span className="font-medium">Saving Time</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-purple-700">
              <Community className="w-4 h-4" />
              <span className="font-medium">Growing Community</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReviewPage
